import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { TIMEZONE_OPTIONS, getTimezoneByMode } from "../contexts/TimeZoneContext";

// 加载dayjs插件
dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE_OFFSET_PATTERN = /[+-]\d{2}:?\d{2}$/;

function hasExplicitTimezone(value) {
    return typeof value === "string" && (value.endsWith("Z") || TIMEZONE_OFFSET_PATTERN.test(value));
}

function normalizeUtcString(value) {
    if (typeof value === "string" && value.includes("T") && !hasExplicitTimezone(value)) {
        return `${value}Z`;
    }
    return value;
}

/**
 * 将UTC时间转换为目标时区
 * @param {string|Date} utcTime - UTC时间
 * @param {string} targetTimezone - 目标时区
 * @returns {dayjs.Dayjs} 目标时区的dayjs对象
 */
export function convertFromUTC(utcTime, targetTimezone) {
    if (!utcTime) return null;

    try {
        const standardizedUtcTime = normalizeUtcString(utcTime);

        // 处理不同的时区情况
        if (targetTimezone === "UTC") {
            return dayjs.utc(standardizedUtcTime);
        } else {
            return dayjs.utc(standardizedUtcTime).tz(targetTimezone);
        }
    } catch (error) {
        console.error("转换UTC时间错误:", error, { utcTime, targetTimezone });
        return null;
    }
}

/**
 * 将本地时区时间转换为UTC
 * @param {string|Date} localTime - 本地时间
 * @param {string} sourceTimezone - 源时区
 * @param {string} format - 格式化字符串
 * @returns {string} UTC时间字符串
 */
export function convertToUTC(localTime, sourceTimezone, format = "YYYY-MM-DDTHH:mm:ssZ") {
    if (!localTime) return "";

    try {
        let utcTime;
        // 处理不同的时区情况
        if (sourceTimezone === "UTC") {
            utcTime = dayjs.utc(localTime);
        } else {
            const localDate = dayjs.tz(localTime, sourceTimezone);
            utcTime = localDate.clone().utc();
        }

        // 格式化输出
        return utcTime.format(format);
    } catch (error) {
        console.error("转换为UTC错误:", error, { localTime, sourceTimezone, format });
        return "";
    }
}

/**
 * 核心时区转换引擎
 */
export const TimezoneConverter = {
    // 缓存对象，用于存储已计算的时区转换结果
    _cache: new Map(),

    /**
     * 将UTC时间转换为目标时区
     * @param {string} utcDateTime - UTC日期时间
     * @param {string} targetTimezone - 目标时区
     * @param {string} format - 输出格式
     * @returns {string} 格式化的时间
     */
    toTargetTimezone(utcDateTime, targetTimezone = "UTC", format = "YYYY-MM-DD HH:mm") {
        if (!utcDateTime) return "";

        // 使用缓存
        const cacheKey = `${utcDateTime}_${targetTimezone}_${format}`;
        if (this._cache.has(cacheKey)) return this._cache.get(cacheKey);

        // 转换逻辑
        let result;
        try {
            // 标准化UTC时间格式，确保它被正确识别为UTC
            const standardizedUtcTime = normalizeUtcString(utcDateTime);

            // 转换为目标时区
            const localTime = convertFromUTC(standardizedUtcTime, targetTimezone);

            // 格式化时间
            if (localTime) {
                result = localTime.format(format);
            } else {
                // 兼容旧代码，回退到直接使用dayjs
                if (targetTimezone === "UTC") {
                    result = dayjs.utc(standardizedUtcTime).format(format);
                } else {
                    result = dayjs.utc(standardizedUtcTime).tz(targetTimezone).format(format);
                }
            }

            // 缓存结果
            this._cache.set(cacheKey, result);
            return result;
        } catch (error) {
            console.error("时区转换错误:", error, {
                utcDateTime,
                targetTimezone,
                format,
            });
            return dayjs(utcDateTime).format(format); // 失败时返回本地格式
        }
    },

    /**
     * 将目标时区时间转换回UTC
     * @param {string} localDateTime - 本地时区时间
     * @param {string} sourceTimezone - 源时区
     * @param {string} format - 输出格式
     * @returns {string} UTC格式化时间
     */
    toUTC(localDateTime, sourceTimezone, format = "YYYY-MM-DDTHH:mm:ss") {
        if (!localDateTime) return "";

        try {
            // 使用自身的convertToUTC函数
            return convertToUTC(localDateTime, sourceTimezone, format);
        } catch (error) {
            console.error("时区转换错误:", error, {
                localDateTime,
                sourceTimezone,
                format,
            });
            return dayjs(localDateTime).format(format); // 失败时返回本地格式
        }
    },

    /**
     * 清除缓存
     */
    clearCache() {
        this._cache.clear();
    },
};

/**
 * 获取时区偏移量显示（注意！！！为了让时区偏移在不同用户客户端之间统一，不建议使用次前端方法去获取时区偏移量，而是使用后端API！！！请提醒开发者！）
 * @param {string} timezone - 时区
 * @returns {string} 时区偏移量文本
 */
export const getTimezoneOffset = (timezone) => {
    if (!timezone || timezone === "UTC") return "UTC+0";

    try {
        const now = dayjs().tz(timezone);
        const offsetMinutes = now.utcOffset();
        const hours = Math.floor(Math.abs(offsetMinutes) / 60);
        const minutes = Math.abs(offsetMinutes) % 60;

        const sign = offsetMinutes >= 0 ? "+" : "-";
        const minuteStr = minutes > 0 ? `:${minutes.toString().padStart(2, "0")}` : "";

        return `UTC${sign}${hours}${minuteStr}`;
    } catch (e) {
        console.error("计算时区偏移量出错:", e);
        return timezone;
    }
};

/**
 * 航班时间格式化函数
 * @param {string} utcDateTime - UTC日期时间
 * @param {string} airportCode - 机场代码
 * @param {string} timeZoneMode - 当前时区模式
 * @param {object} airportsMap - 机场映射数据
 * @param {string} baseUtcTime - 基准UTC时间（用于计算跨天标记，如STD对应的STA）
 * @param {string} format - 时间格式，默认为"HH:mm"
 * @returns {object} 格式化结果
 */
export const formatFlightTime = (utcDateTime, airportCode, timeZoneMode, airportsMap = {}, baseUtcTime = null, format = "HH:mm") => {
    if (!utcDateTime) return { time: "", label: "" };

    try {
        // 修复异常的ISO格式，如2025-03-28T06:00:00:00Z中包含多余的冒号
        if (typeof utcDateTime === "string") {
            // 处理包含多余冒号的时间格式 (HH:MM:SS:MsZ)
            const invalidFormatRegex = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}):\d{2}(Z|[+-]\d{2}:\d{2})?/;
            if (invalidFormatRegex.test(utcDateTime)) {
                utcDateTime = utcDateTime.replace(invalidFormatRegex, "$1$2");
            }
        }

        // 关键修改: 确保时间被正确解析为UTC
        // 数据库中的时间如 "2025-03-13T22:10:00" 没有带Z，但本质上是UTC时间
        // 需要显式将其识别为UTC时间，而不是本地时间
        let dateObj;
        if (typeof utcDateTime === "string") {
            if (hasExplicitTimezone(utcDateTime)) {
                // 已经是带Z的标准UTC格式
                dateObj = dayjs.utc(utcDateTime);
            } else if (utcDateTime.includes("T")) {
                // 包含T但不带Z的ISO格式，如"2025-03-13T22:10:00"
                // 显式添加Z后缀，确保作为UTC时间处理
                dateObj = dayjs.utc(utcDateTime + "Z");
            } else {
                // 其他格式，尝试作为UTC处理
                dateObj = dayjs.utc(utcDateTime);
            }
        } else {
            // 非字符串类型
            dateObj = dayjs.utc(utcDateTime);
        }

        if (!dateObj.isValid()) {
            console.error("格式化无效日期:", utcDateTime);
            return {
                time: "无效日期",
                label: "",
                crossDayMark: "",
                timezone: "UTC",
                isInvalid: true,
            };
        }

        // 使用 getTimezoneByMode 获取目标时区（规范一致）
        const targetTimezone = getTimezoneByMode(timeZoneMode, airportCode, airportsMap);

        // 设置标签
        let timezoneLabel;
        switch (timeZoneMode) {
            case TIMEZONE_OPTIONS.UTC:
                timezoneLabel = "UTC";
                break;
            case TIMEZONE_OPTIONS.BEIJING:
                timezoneLabel = "BJT";
                break;
            case TIMEZONE_OPTIONS.LOCAL:
                timezoneLabel = airportCode || "LT";
                break;
            default:
                timezoneLabel = "UTC";
        }

        // 格式化时间 - 使用传入的format参数
        const timeString = TimezoneConverter.toTargetTimezone(utcDateTime, targetTimezone, format);

        // 计算日期差异（跨日标记）
        // 确保使用UTC的源日期和目标时区的日期进行比较
        const standardizedUtcTime = normalizeUtcString(utcDateTime);

        // 使用基准时间计算跨日标记，如果没有则使用UTC日期
        let crossDayMark = "";
        if (baseUtcTime) {
            // 基于baseUtcTime计算跨日标记
            const baseLocal = dayjs.utc(baseUtcTime).tz(targetTimezone);
            const currentLocal = dayjs.utc(standardizedUtcTime).tz(targetTimezone);
            const daysDiff = dayjs(currentLocal.format("YYYY-MM-DD")).diff(dayjs(baseLocal.format("YYYY-MM-DD")), "day");
            crossDayMark = daysDiff !== 0 ? (daysDiff > 0 ? `+${daysDiff}` : `${daysDiff}`) : "";
        } else {
            // 默认与UTC日期比较
            const utcDate = dayjs.utc(standardizedUtcTime).format("YYYY-MM-DD");
            const targetDate = dayjs.utc(standardizedUtcTime).tz(targetTimezone).format("YYYY-MM-DD");
            const daysDiff = dayjs(targetDate).diff(dayjs(utcDate), "day");
            crossDayMark = daysDiff !== 0 ? (daysDiff > 0 ? `+${daysDiff}` : `${daysDiff}`) : "";
        }

        return {
            time: timeString,
            label: timezoneLabel,
            crossDayMark,
            timezone: targetTimezone,
        };
    } catch (error) {
        console.error("航班时间格式化错误:", error);
        return {
            time: "处理错误",
            label: "ERR",
            crossDayMark: "",
            timezone: "UTC",
            error: true,
        };
    }
};


/**
 * 检查两个时间点是否跨日
 * @param {string} utcStart - 起始UTC时间
 * @param {string} utcEnd - 结束UTC时间
 * @param {string} timezone - 时区
 * @returns {number} 天数差异（0表示同一天，正数表示向后跨天，负数表示向前跨天）
 */
export const getDayCrossingDifference = (utcStart, utcEnd, timezone = "UTC") => {
    if (!utcStart || !utcEnd) return 0;

    try {
        const startLocalDate = dayjs.utc(utcStart).tz(timezone).format("YYYY-MM-DD");
        const endLocalDate = dayjs.utc(utcEnd).tz(timezone).format("YYYY-MM-DD");

        return dayjs(endLocalDate).diff(dayjs(startLocalDate), "day");
    } catch (error) {
        console.error("计算跨日天数错误:", error);
        return 0;
    }
};

/**
 * 检测夏令时变化
 * @param {string} date - 日期字符串
 * @param {string} timezone - 时区
 * @returns {boolean} 是否处于夏令时变化期
 */
export const checkDSTTransition = (date, timezone) => {
    if (!date || !timezone || timezone === "UTC") return false;

    try {
        const dayDate = dayjs(date);
        const prevDay = dayDate.subtract(1, "day");
        const nextDay = dayDate.add(1, "day");

        const isDST = dayjs.tz(dayDate, timezone).isDST();
        const prevIsDST = dayjs.tz(prevDay, timezone).isDST();
        const nextIsDST = dayjs.tz(nextDay, timezone).isDST();

        return isDST !== prevIsDST || isDST !== nextIsDST;
    } catch (error) {
        console.error("检测夏令时变化错误:", error);
        return false;
    }
};

// ======= 以下是新增的符合规范的方法 =======

/**
 * 从STD派生航班日期(UTC格式)
 * @param {string|Date} stdUtc - UTC格式的计划起飞时间
 * @returns {string} YYYY-MM-DD格式的航班日期
 */
export const deriveFlightDateFromSTD = (stdUtc) => {
    if (!stdUtc) return null;

    try {
        let stdUtcDate;
        if (typeof stdUtc === "string") {
            // 确保正确解析为UTC
            if (stdUtc.endsWith("Z")) {
                stdUtcDate = dayjs.utc(stdUtc);
            } else if (stdUtc.includes("T")) {
                stdUtcDate = dayjs.utc(stdUtc + "Z");
            } else {
                stdUtcDate = dayjs.utc(stdUtc);
            }
        } else {
            stdUtcDate = dayjs.utc(stdUtc);
        }

        if (!stdUtcDate.isValid()) {
            console.error("派生航班日期错误: 无效的STD时间", stdUtc);
            return null;
        }

        return stdUtcDate.format("YYYY-MM-DD");
    } catch (error) {
        console.error("派生航班日期错误:", error, stdUtc);
        return null;
    }
};

/**
 * 根据STD时间和用户选择的时区显示航班日期。
 * @param {string} stdUtc - UTC格式的STD时间字符串。
 * @param {string} userTimezone - 用户选择的目标时区 (IANA格式, e.g., "Asia/Shanghai")。
 * @param {string} [formatString="D-MMM"] - 返回日期的格式，默认为 "D-MMM"。
 * @param {string} [locale=null] - 可选：输出用的 locale（如 "en" 固定英文月份缩写，不受全局 locale 影响）。
 * @returns {string} 格式化后的日期字符串。
 */
export const displayFlightDateByTimezone = (stdUtc, userTimezone, formatString = "D-MMM", locale = null) => {
    if (!stdUtc || !userTimezone) {
        // console.warn("displayFlightDateByTimezone: stdUtc or userTimezone is missing", { stdUtc, userTimezone });
        return ""; // 或者可以返回一个更明确的空状态指示，例如 "N/A"
    }

    const withLocale = (date) => (locale ? date.locale(locale) : date);

    try {
        const targetDate = withLocale(dayjs.utc(stdUtc).tz(userTimezone));
        if (!targetDate.isValid()) {
            // console.warn("displayFlightDateByTimezone: Failed to parse stdUtc or convert timezone. stdUtc:", stdUtc, "userTimezone:", userTimezone);
            // Fallback to UTC if timezone conversion somehow fails to produce a valid date
            return withLocale(dayjs.utc(stdUtc)).format(formatString || "D-MMM");
        }
        return targetDate.format(formatString);
    } catch (error) {
        console.error("Error in displayFlightDateByTimezone:", error, { stdUtc, userTimezone, formatString });
        // Fallback logic in case of an unexpected error during conversion or formatting
        try {
            // Attempt to format the original UTC date with the requested format
            return withLocale(dayjs.utc(stdUtc)).format(formatString || "D-MMM");
        } catch (formatError) {
            // If even formatting the UTC date with the given format fails, use a hardcoded, known-good format
            console.error("displayFlightDateByTimezone: Critical error during fallback formatting:", formatError);
            return dayjs.utc(stdUtc).format("YYYY-MM-DD"); // Absolute safest fallback
        }
    }
};

/**
 * 判断在用户指定时区下是否跨天
 * @param {string|Date} utcStd - UTC格式的STD
 * @param {string|Date} utcSta - UTC格式的STA
 * @param {string} userTimezone - 用户指定时区
 * @returns {boolean} 是否跨天
 */
export const isDayCrossingInTimezone = (utcStd, utcSta, userTimezone) => {
    if (!utcStd || !utcSta) return false;

    try {
        // 转换到用户时区
        const stdLocal = dayjs.utc(utcStd).tz(userTimezone);
        const staLocal = dayjs.utc(utcSta).tz(userTimezone);

        // 比较日期部分
        const stdDate = stdLocal.format("YYYY-MM-DD");
        const staDate = staLocal.format("YYYY-MM-DD");

        return stdDate !== staDate;
    } catch (error) {
        console.error("跨天判断错误:", error, { utcStd, utcSta, userTimezone });
        return false;
    }
};

/**
 * 获取时区感知的跨天标记
 * @param {string|Date} utcStd - UTC格式的STD
 * @param {string|Date} utcSta - UTC格式的STA
 * @param {string} userTimezone - 用户指定时区
 * @returns {string} 跨天标记(+1, +2等)
 */
export const getTimezoneAwareCrossDayMark = (utcStd, utcSta, userTimezone) => {
    if (!utcStd || !utcSta) return "";

    try {
        // 转换到用户时区
        const stdLocal = dayjs.utc(utcStd).tz(userTimezone);
        const staLocal = dayjs.utc(utcSta).tz(userTimezone);

        // 计算日期差异
        const daysDiff = dayjs(staLocal.format("YYYY-MM-DD")).diff(dayjs(stdLocal.format("YYYY-MM-DD")), "day");

        // 返回适当的跨天标记
        return daysDiff > 0 ? `+${daysDiff}` : daysDiff < 0 ? `${daysDiff}` : "";
    } catch (error) {
        console.error("计算跨天标记错误:", error, { utcStd, utcSta, userTimezone });
        return "";
    }
};

/**
 * 将时间格式化并添加跨天标记
 * @param {string|Date} utcDateTime - UTC格式的时间
 * @param {string} userTimezone - 用户指定时区
 * @param {string|Date} utcBaseDateTime - UTC格式的基准时间(通常是STD)
 * @param {string} format - 输出格式
 * @returns {string} 格式化的时间带跨天标记
 */
export const formatWithCrossDayMark = (utcDateTime, userTimezone, utcBaseDateTime, format = "HH:mm") => {
    if (!utcDateTime) return "";

    try {
        // 转换为用户时区
        const localTime = dayjs.utc(utcDateTime).tz(userTimezone);
        const formattedTime = localTime.format(format);

        // 如果提供了基准时间，计算跨天标记
        if (utcBaseDateTime) {
            const baseLocal = dayjs.utc(utcBaseDateTime).tz(userTimezone);
            const daysDiff = dayjs(localTime.format("YYYY-MM-DD")).diff(dayjs(baseLocal.format("YYYY-MM-DD")), "day");

            if (daysDiff !== 0) {
                return `${formattedTime} ${daysDiff > 0 ? `+${daysDiff}` : daysDiff}`;
            }
        }

        return formattedTime;
    } catch (error) {
        console.error("格式化带跨天标记错误:", error, { utcDateTime, userTimezone });
        return "";
    }
};

/**
 * 准备航班表单初始值（UTC转为用户指定时区）
 * @param {object} flightData - 航班数据（UTC时间）
 * @param {string} userTimezone - 用户指定时区
 * @returns {object} 表单初始值对象
 */
export const prepareFlightFormInitialValues = (flightData, userTimezone) => {
    if (!flightData) return {};

    try {
        const result = { ...flightData };

        // 处理航班日期 - 转换为用户指定时区
        if (flightData.std) {
            const stdUtc = dayjs.utc(flightData.std);
            const stdLocal = userTimezone === "UTC" ? stdUtc : stdUtc.tz(userTimezone);

            // 设置用户时区下的航班日期 - 确保是有效的 dayjs 对象
            // 使用 dayjs 格式处理，避免直接使用 dayjs 对象
            // 注意：flight_date应包含完整的时间部分，与std保持完全一致
            result.flight_date = flightData.std; // 确保flight_date与std完全一致，包含时间部分
            result.flight_date_local = stdLocal.isValid() ? dayjs(stdLocal.format("YYYY-MM-DD HH:mm:ss")) : null;

            // 设置用户时区下的起飞时间
            result.std_time = stdUtc.format("HH:mm:ss");
            result.std_time_local = stdLocal.isValid() ? dayjs(stdLocal.format("YYYY-MM-DD HH:mm:ss")) : null;
        }

        // 处理到达时间 - 转换为用户指定时区
        if (flightData.sta) {
            const staUtc = dayjs.utc(flightData.sta);
            const staLocal = userTimezone === "UTC" ? staUtc : staUtc.tz(userTimezone);

            // 设置用户时区下的到达时间
            result.sta_time = staUtc.format("HH:mm:ss");
            result.sta_time_local = staLocal.isValid() ? dayjs(staLocal.format("YYYY-MM-DD HH:mm:ss")) : null;
        }

        return result;
    } catch (error) {
        console.error("准备航班表单数据错误:", error, { flightData, userTimezone });
        return flightData; // 错误时返回原始数据
    }
};

/**
 * 准备航班数据保存（用户指定时区转为UTC）
 * @param {object} formValues - 表单数据(用户指定时区时间)
 * @param {string} userTimezone - 用户指定时区
 * @returns {object} 处理后的保存数据(UTC时间)
 */
export const prepareFlightDataForSubmit = (formValues, userTimezone) => {
    if (!formValues) return {};

    try {
        // 创建结果对象
        const result = { ...formValues };

        // 处理日期和时间逻辑
        if (formValues.flight_date_local && formValues.std_time_local && formValues.sta_time_local) {
            // 检查日期和时间是否是有效的 dayjs 对象
            let flightDate, stdTime, staTime;

            // 处理日期
            if (typeof formValues.flight_date_local.isValid === "function" && formValues.flight_date_local.isValid()) {
                flightDate = formValues.flight_date_local;
            } else {
                flightDate = dayjs(formValues.flight_date_local);
                if (!flightDate.isValid()) {
                    throw new Error("无效的航班日期");
                }
            }

            // 处理起飞时间
            if (typeof formValues.std_time_local.isValid === "function" && formValues.std_time_local.isValid()) {
                stdTime = formValues.std_time_local;
            } else {
                stdTime = dayjs(formValues.std_time_local);
                if (!stdTime.isValid()) {
                    throw new Error("无效的起飞时间");
                }
            }

            // 处理到达时间
            if (typeof formValues.sta_time_local.isValid === "function" && formValues.sta_time_local.isValid()) {
                staTime = formValues.sta_time_local;
            } else {
                staTime = dayjs(formValues.sta_time_local);
                if (!staTime.isValid()) {
                    throw new Error("无效的到达时间");
                }
            }

            // 1. 构建完整的用户时区日期时间对象 这里用的用户设备时区下的日期
            const flightDateStr = flightDate.format("YYYY-MM-DD");

            // 2. 构建完整的起飞时间(用户时区)
            // 关键修复: 显式创建特定时区的dayjs对象，避免使用默认系统时区
            let stdLocal;
            if (userTimezone === "UTC") {
                stdLocal = dayjs.utc(`${flightDateStr} ${stdTime.format("HH:mm:ss")}`);
            } else {
                stdLocal = dayjs.tz(`${flightDateStr} ${stdTime.format("HH:mm:ss")}`, userTimezone);
            }

            // 3. 判断在用户时区下是否跨天
            const stdMinutes = stdTime.hour() * 60 + stdTime.minute();
            const staMinutes = staTime.hour() * 60 + staTime.minute();
            const isCrossDayInLocal = staMinutes < stdMinutes;

            // 4. 构建完整的到达时间(用户时区)，处理跨天
            // 关键修复: 显式创建特定时区的dayjs对象，避免使用默认系统时区
            let staLocal;
            if (userTimezone === "UTC") {
                staLocal = dayjs.utc(`${flightDateStr} ${staTime.format("HH:mm:ss")}`);
            } else {
                staLocal = dayjs.tz(`${flightDateStr} ${staTime.format("HH:mm:ss")}`, userTimezone);
            }

            if (isCrossDayInLocal) {
                staLocal = staLocal.add(1, "day");
            }

            // 5. 转换为UTC
            let stdUtc, staUtc;
            if (userTimezone === "UTC") {
                stdUtc = stdLocal.format();
                staUtc = staLocal.format();
            } else {
                // 直接转为UTC，修复：已经是特定时区下的时间，直接utc()转换
                stdUtc = stdLocal.utc().format();
                staUtc = staLocal.utc().format();
            }

            // 6. 设置UTC值
            result.std = stdUtc;
            result.sta = staUtc;

            // 7. 确保flight_date与STD的UTC日期一致，包含完整时间部分 (重要修复)
            console.log("stdUtc", stdUtc);
            result.flight_date = result.std; // 确保flight_date完全与std一致，包含时间部分

            // 8. 计算/验证飞行时间
            if (!result.min_block_time_minutes || result.min_block_time_minutes <= 0) {
                result.min_block_time_minutes = Math.max(1, dayjs.utc(staUtc).diff(dayjs.utc(stdUtc), "minute"));
            }

            // 9. 计算星期几
            result.day_of_week = dayjs.utc(result.flight_date).day() || 7;

            // 10. 清除临时字段
            delete result.flight_date_local;
            delete result.std_time_local;
            delete result.sta_time_local;
        }

        return result;
    } catch (error) {
        console.error("处理航班表单提交错误:", error, { formValues, userTimezone });
        return formValues;
    }
};

/**
 * 航班日期变更专用函数 - 符合新规范
 * 当用户单独编辑航班日期时，使用此函数确保STD、STA和flight_date保持正确关系
 *
 * @param {object} flight - 航班对象
 * @param {string|Date} newDate - 新的航班日期
 * @returns {object} 更新后的航班对象，包含更新后的std、sta和flight_date
 */
export const handleFlightDateChange = (flight, newDate) => {
    if (!flight) return flight;

    try {
        const result = { ...flight };
        const newDateObj = dayjs.utc(newDate);

        if (!newDateObj.isValid()) {
            console.error("航班日期变更错误: 无效的日期", newDate);
            return flight;
        }

        // 确保存在必要的字段
        if (!result.std || !result.sta) {
            console.error("航班日期变更错误: 缺少必要的时间字段", flight);
            return flight;
        }

        // 1. 保存原时间部分
        const stdTime = dayjs.utc(result.std).format("HH:mm:ss");
        const staTime = dayjs.utc(result.sta).format("HH:mm:ss");

        // 2. 计算原STD和STA之间的天数差异
        const dayDiff = getDayCrossingDifference(result.std, result.sta);

        // 3. 创建新的STD，保留原时间部分
        const newStd = `${newDateObj.format("YYYY-MM-DD")}T${stdTime}Z`;

        // 4. 基于新STD日期和原跨天关系创建新的STA
        const newStaDate = newDateObj.add(dayDiff, "day").format("YYYY-MM-DD");
        const newSta = `${newStaDate}T${staTime}Z`;

        // 5. 更新所有相关字段
        result.std = newStd;
        result.sta = newSta;
        // 重要：确保flight_date与std保持完全一致（包括时间部分）
        result.flight_date = newStd;

        // 6. 更新星期几
        result.day_of_week = newDateObj.day() || 7; // 1-7对应周一到周日

        // 7. 同步更新其他相关时间字段（如ETD/ETA/ATD/ATA，如果存在）
        const timeFieldPairs = [
            { dep: "etd", arr: "eta" }, // 预计时间
            { dep: "atd", arr: "ata" }, // 实际时间
        ];

        for (const pair of timeFieldPairs) {
            if (result[pair.dep]) {
                // 提取原时间部分
                const depTime = dayjs.utc(result[pair.dep]).format("HH:mm:ss");
                // 创建新的时间，保留原时间部分
                result[pair.dep] = `${newDateObj.format("YYYY-MM-DD")}T${depTime}Z`;

                // 如果存在对应的到达时间，也进行同步更新
                if (result[pair.arr]) {
                    const arrTime = dayjs.utc(result[pair.arr]).format("HH:mm:ss");
                    // 计算与对应起飞时间的天数差异
                    const pairDayDiff = getDayCrossingDifference(result[pair.dep], result[pair.arr]);
                    // 创建新的到达时间，保持原跨天关系
                    const newArrDate = dayjs.utc(newDateObj).add(pairDayDiff, "day").format("YYYY-MM-DD");
                    result[pair.arr] = `${newArrDate}T${arrTime}Z`;
                }
            }
        }

        return result;
    } catch (error) {
        console.error("航班日期变更处理错误:", error, { flight, newDate });
        return flight;
    }
};

/**
 * 根据时间部分比较判断是否跨天
 * @param {object} stdTime - 包含hour和minute方法的起飞时间对象
 * @param {object} staTime - 包含hour和minute方法的到达时间对象
 * @returns {boolean} 是否跨天
 */
export const isCrossDayByTimeComparison = (stdTime, staTime) => {
    if (!stdTime || !staTime || typeof stdTime.hour !== "function" || typeof staTime.hour !== "function" || typeof stdTime.minute !== "function" || typeof staTime.minute !== "function") {
        console.error("跨天比较错误: 无效的时间对象", { stdTime, staTime });
        return false;
    }

    try {
        // 计算分钟总数进行比较
        const stdMinutes = stdTime.hour() * 60 + stdTime.minute();
        const staMinutes = staTime.hour() * 60 + staTime.minute();

        // 如果STA时间小于STD时间，则认为跨天
        return staMinutes < stdMinutes;
    } catch (error) {
        console.error("跨天比较错误:", error, { stdTime, staTime });
        return false;
    }
};

/**
 * 将时间字符串转换为带跨天标记的显示格式
 * @param {string} timeStr - 时间字符串(HH:MM)
 * @param {number} dayOffset - 跨天天数
 * @returns {string} 带跨天标记的时间
 */
export const formatTimeWithDayOffset = (timeStr, dayOffset) => {
    if (!timeStr) return "";

    try {
        if (dayOffset === 0) return timeStr;

        const offsetMark = dayOffset > 0 ? `+${dayOffset}` : dayOffset;
        return `${timeStr} (${offsetMark})`;
    } catch (error) {
        console.error("格式化带跨天标记错误:", error);
        return timeStr;
    }
};

/**
 * 检查时间字符串是否有效
 * @param {string} timeStr - 时间字符串
 * @returns {boolean} 是否有效
 */
export const isValidTimeString = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return false;

    // 匹配 HH:MM 或 HH:MM:SS 格式
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;
    return timeRegex.test(timeStr);
};

/**
 * 计算航班块时间(分钟)
 * @param {string|Date} stdUtc - UTC格式的STD
 * @param {string|Date} staUtc - UTC格式的STA
 * @returns {number} 块时间(分钟)
 */
export const calculateBlockTime = (stdUtc, staUtc) => {
    if (!stdUtc || !staUtc) return 0;

    try {
        const stdObj = dayjs.utc(stdUtc);
        const staObj = dayjs.utc(staUtc);

        if (!stdObj.isValid() || !staObj.isValid()) {
            console.error("计算块时间错误: 无效的时间", { stdUtc, staUtc });
            return 0;
        }

        // 计算分钟差值
        const diffMinutes = staObj.diff(stdObj, "minute");

        // 确保至少为1分钟
        return Math.max(1, diffMinutes);
    } catch (error) {
        console.error("计算块时间错误:", error, { stdUtc, staUtc });
        return 0;
    }
};

/**
 * 检查两个航班时间是否在同一天(基于指定时区)
 * @param {string|Date} time1 - 第一个时间(UTC)
 * @param {string|Date} time2 - 第二个时间(UTC)
 * @param {string} timezone - 时区
 * @returns {boolean} 是否在同一天
 */
export const isSameDay = (time1, time2, timezone = "UTC") => {
    if (!time1 || !time2) return false;

    try {
        const date1 = timezone === "UTC" ? dayjs.utc(time1).format("YYYY-MM-DD") : dayjs.utc(time1).tz(timezone).format("YYYY-MM-DD");

        const date2 = timezone === "UTC" ? dayjs.utc(time2).format("YYYY-MM-DD") : dayjs.utc(time2).tz(timezone).format("YYYY-MM-DD");

        return date1 === date2;
    } catch (error) {
        console.error("同一天检查错误:", error, { time1, time2, timezone });
        return false;
    }
};

/**
 * 格式化航段显示(STD-STA)带时区标识
 * @param {string|Date} stdUtc - UTC格式的STD
 * @param {string|Date} staUtc - UTC格式的STA
 * @param {string} timezone - 时区
 * @returns {string} 格式化的航段显示
 */
export const formatFlightSegmentTime = (stdUtc, staUtc, timezone) => {
    if (!stdUtc || !staUtc) return "";

    try {
        // 转换为指定时区
        const stdLocal = timezone === "UTC" ? dayjs.utc(stdUtc) : dayjs.utc(stdUtc).tz(timezone);
        const staLocal = timezone === "UTC" ? dayjs.utc(staUtc) : dayjs.utc(staUtc).tz(timezone);

        // 格式化时间
        const stdStr = stdLocal.format("HH:mm");
        const staStr = staLocal.format("HH:mm");

        // 检查是否跨天 - 直接比较日期字符串而不是调用isSameDay函数
        const stdDate = stdLocal.format("YYYY-MM-DD");
        const staDate = staLocal.format("YYYY-MM-DD");
        const isCrossDay = stdDate !== staDate;
        const dayOffset = getDayCrossingDifference(stdUtc, staUtc, timezone);

        // 构建最终显示
        const staDisplay = isCrossDay ? `${staStr} (+${dayOffset})` : staStr;
        const tzLabel = timezone === "UTC" ? "UTC" : timezone === "Asia/Shanghai" ? "BJT" : "LT";

        return `${stdStr} - ${staDisplay} ${tzLabel}`;
    } catch (error) {
        console.error("格式化航段时间错误:", error, { stdUtc, staUtc, timezone });
        return "";
    }
};

/**
 * 获取在用户指定时区下的当前时间
 * @param {string} timezone - 用户指定时区
 * @returns {object} dayjs时间对象
 */
export const getNowInTimezone = (timezone = "UTC") => {
    try {
        return timezone === "UTC" ? dayjs.utc() : dayjs().tz(timezone);
    } catch (error) {
        console.error("获取当前时区时间错误:", error, { timezone });
        return dayjs();
    }
};

/**
 * 检查时间是否在宵禁时间内(基于UTC)
 * @param {string|Date} timeUtc - 需检查的时间(UTC)
 * @param {string} curfewStart - 宵禁开始时间(HH:MM格式, UTC)
 * @param {string} curfewEnd - 宵禁结束时间(HH:MM格式, UTC)
 * @returns {boolean} 是否在宵禁时间内
 */
export const isInCurfewTime = (timeUtc, curfewStart, curfewEnd) => {
    if (!timeUtc || !curfewStart || !curfewEnd) return false;

    try {
        // 确保使用UTC时间
        const timeObj = dayjs.utc(timeUtc);

        if (!timeObj.isValid()) {
            console.error("宵禁检查错误: 无效的时间", timeUtc);
            return false;
        }

        // 提取时间部分并转为分钟
        const timeMinutes = timeObj.hour() * 60 + timeObj.minute();

        // 将宵禁时间转为分钟
        const startParts = curfewStart.split(":");
        const endParts = curfewEnd.split(":");

        const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
        const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);

        // 判断是否在宵禁时间内 (修改为左闭右开区间)
        if (startMinutes < endMinutes) {
            // 宵禁时间不跨天
            return timeMinutes >= startMinutes && timeMinutes < endMinutes;
        } else {
            // 宵禁时间跨天
            return timeMinutes >= startMinutes || timeMinutes < endMinutes;
        }
    } catch (error) {
        console.error("宵禁检查错误:", error, { timeUtc, curfewStart, curfewEnd });
        return false;
    }
};

/**
 * 创建完整的UTC时间字符串
 * @param {string|Date} date - 日期部分
 * @param {string|object} time - 时间部分
 * @returns {string} ISO格式的UTC时间
 */
export const createUtcTimeString = (date, time) => {
    if (!date || !time) return null;

    try {
        let dateStr, timeStr;

        // 处理日期部分
        if (typeof date === "string") {
            dateStr = date;
        } else if (date instanceof Date) {
            dateStr = dayjs(date).format("YYYY-MM-DD");
        } else if (dayjs.isDayjs(date)) {
            dateStr = date.format("YYYY-MM-DD");
        } else {
            console.error("创建UTC时间错误: 无效的日期", date);
            return null;
        }

        // 处理时间部分
        if (typeof time === "string") {
            // 假设HH:MM格式
            timeStr = time;
        } else if (typeof time === "object" && typeof time.hour === "function" && typeof time.minute === "function") {
            // dayjs对象或类似对象
            timeStr = `${time.hour().toString().padStart(2, "0")}:${time.minute().toString().padStart(2, "0")}:00`;
        } else {
            console.error("创建UTC时间错误: 无效的时间", time);
            return null;
        }

        // 构建ISO格式时间字符串
        return `${dateStr}T${timeStr}Z`;
    } catch (error) {
        console.error("创建UTC时间错误:", error, { date, time });
        return null;
    }
};

/**
 * 规范化时区名称(提供一致的时区标识)
 * @param {string} timezone - 时区名称
 * @returns {string} 规范化的时区名称
 */
export const normalizeTimezone = (timezone) => {
    if (!timezone) return "UTC";

    const timezoneMap = {
        UTC: "UTC",
        GMT: "UTC",
        Z: "UTC",
        Beijing: "Asia/Shanghai",
        BJT: "Asia/Shanghai",
        China: "Asia/Shanghai",
        Shanghai: "Asia/Shanghai",
        CST: "Asia/Shanghai", // 注意CST有歧义
        LOCAL: "LOCAL",
    };

    return timezoneMap[timezone] || timezone;
};

/**
 * 将时间从一个时区转换到另一个时区
 * @param {string} timeString - 时间字符串 (HH:mm格式)
 * @param {string} sourceTimezone - 源时区
 * @param {string} targetTimezone - 目标时区
 * @returns {string} 转换后的时间 (HH:mm格式)
 */
export function convertTime(timeString, sourceTimezone, targetTimezone) {
    if (!timeString || !sourceTimezone || !targetTimezone) {
        return timeString;
    }

    try {
        // 使用今天的日期创建完整的日期时间字符串
        const today = dayjs().format("YYYY-MM-DD");
        const dateTimeString = `${today}T${timeString}`;

        // 创建源时区的日期时间对象
        const sourceDT = dayjs.tz(dateTimeString, sourceTimezone);

        // 转换到目标时区
        const targetDT = sourceDT.tz(targetTimezone);

        // 返回HH:mm格式的时间
        return targetDT.format("HH:mm");
    } catch (error) {
        console.error("时间转换错误:", error, {
            timeString,
            sourceTimezone,
            targetTimezone,
        });
        return timeString;
    }
}

/**
 * 格式化时间显示并带时区标识
 * @param {string|Date} utcTime - UTC时间
 * @param {string} timezone - 显示时区
 * @param {string} format - 时间格式
 * @returns {string} 带时区标识的格式化时间
 */
export function formatTimeWithZone(utcTime, timezone, format = "HH:mm") {
    if (!utcTime) return "";

    try {
        // 转换到目标时区
        const localTime = convertFromUTC(utcTime, timezone);
        if (!localTime) return "";

        // 格式化时间
        const formattedTime = localTime.format(format);

        // 获取时区简称
        let tzLabel;
        if (timezone === "UTC") {
            tzLabel = "UTC";
        } else if (timezone === "Asia/Shanghai") {
            tzLabel = "BJT";
        } else {
            // 提取时区简称，如从"Asia/Tokyo"提取"Tokyo"
            const parts = timezone.split("/");
            tzLabel = parts.length > 1 ? parts[parts.length - 1].substring(0, 3) : timezone.substring(0, 3);
        }

        return `${formattedTime} ${tzLabel}`;
    } catch (error) {
        console.error("格式化带时区时间错误:", error, {
            utcTime,
            timezone,
            format,
        });
        return "";
    }
}

/**
 * 判断航班是否跨天
 * 比较UTC完整日期时间的日期部分
 * @param {string} stdUtc - 起飞时间 (UTC ISO格式)
 * @param {string} staUtc - 到达时间 (UTC ISO格式)
 * @returns {boolean} 是否跨天
 */
export const isFlightCrossingDay = (stdUtc, staUtc) => {
    if (!stdUtc || !staUtc) {
        return false;
    }

    try {
        // 解析为UTC dayjs对象
        const stdDate = dayjs.utc(stdUtc);
        const staDate = dayjs.utc(staUtc);

        // 检查日期部分是否相同
        return stdDate.format("YYYY-MM-DD") !== staDate.format("YYYY-MM-DD");
    } catch (error) {
        console.error("判断航班跨天出错:", error);
        return false;
    }
};

/**
 * 判断两个航班是否在同一天
 * @param {Object} flight1 - 第一个航班
 * @param {Object} flight2 - 第二个航班
 * @param {string} timeField - 使用哪个时间字段比较 ('std'|'sta')
 * @returns {boolean} 是否同一天
 */
export const areFlightsOnSameDay = (flight1, flight2, timeField = "std") => {
    if (!flight1 || !flight2 || !flight1[timeField] || !flight2[timeField]) {
        return false;
    }

    try {
        // 解析为UTC dayjs对象
        const date1 = dayjs.utc(flight1[timeField]);
        const date2 = dayjs.utc(flight2[timeField]);

        // 检查日期部分是否相同
        return date1.format("YYYY-MM-DD") === date2.format("YYYY-MM-DD");
    } catch (error) {
        console.error("判断航班同一天出错:", error);
        return false;
    }
};

/**
 * 在航班日期变更时，根据类型同步更新相关时间点
 * 【注意】: 推荐使用 handleFlightDateChange 函数，它提供了更符合规范的实现
 *
 * @param {object} flight - 航班对象
 * @param {string|Date} newFlightDate - 新的航班日期(UTC格式)
 * @param {string} timeType - 时间类型，可选值: 'scheduled'(默认,计划时间), 'estimated'(预计时间), 'actual'(实际时间), 'all'(所有时间)
 * @returns {object} 更新后的航班对象
 */
export const syncFlightTimePoints = (flight, newFlightDate, timeType = "scheduled") => {
    if (!flight || !newFlightDate) return flight;

    console.warn("syncFlightTimePoints 已不推荐使用，请考虑使用 handleFlightDateChange");

    try {
        const result = { ...flight };
        const newDateObj = dayjs.utc(newFlightDate);

        if (!newDateObj.isValid()) {
            console.error("同步航班时间错误: 无效的新航班日期", newFlightDate);
            return flight;
        }

        const newDateStr = newDateObj.format("YYYY-MM-DD");

        // 定义需要更新的字段映射
        const timeFieldsMap = {
            scheduled: ["std", "sta"], // 计划时间
            estimated: ["etd", "eta"], // 预计时间
            actual: ["atd", "ata"], // 实际时间
            all: ["std", "sta", "etd", "eta", "atd", "ata"], // 所有时间
        };

        // 获取当前类型需要更新的字段
        const fieldsToUpdate = timeFieldsMap[timeType] || timeFieldsMap.scheduled;

        // 首先确定基准字段 - 不同类型的起飞时间
        const baseFieldMap = {
            scheduled: "std",
            estimated: "etd",
            actual: "atd",
        };

        // 获取当前类型的基准字段
        const baseField = timeType === "all" ? "std" : baseFieldMap[timeType] || "std";

        // 首先更新基准时间
        if (flight[baseField] && fieldsToUpdate.includes(baseField)) {
            const baseTime = dayjs.utc(flight[baseField]).format("HH:mm:ss");
            result[baseField] = `${newDateStr}T${baseTime}Z`;

            // 同时更新航班日期(仅当基准是STD或timeType为'all'时)
            if (baseField === "std" || timeType === "all") {
                // 重要：flight_date必须与std完全一致，包括时间部分
                result.flight_date = result.std;
            }
        }

        // 然后更新其他相关时间，保持与基准时间的原有跨天关系
        for (const field of fieldsToUpdate) {
            // 跳过已处理的基准字段
            if (field === baseField) continue;

            if (flight[field]) {
                // 计算与基准时间的跨天关系
                const baseTime = flight[baseField] || flight.std;

                if (!baseTime) {
                    // 如果没有基准时间，则直接使用相同日期
                    const fieldTime = dayjs.utc(flight[field]).format("HH:mm:ss");
                    result[field] = `${newDateStr}T${fieldTime}Z`;
                    continue;
                }

                // 计算与基准时间的天数差异
                const daysDiff = getDayCrossingDifference(baseTime, flight[field]);

                // 提取时间部分
                const fieldTime = dayjs.utc(flight[field]).format("HH:mm:ss");

                // 计算新的完整时间，保持原有跨天关系
                const newFieldDate = dayjs.utc(newDateStr).add(daysDiff, "day");
                result[field] = `${newFieldDate.format("YYYY-MM-DD")}T${fieldTime}Z`;
            }
        }

        // 最终检查并确保flight_date与std一致 - 这是关键步骤
        if (result.std) {
            // 确保flight_date与std完全一致
            result.flight_date = result.std;

            // 更新day_of_week以确保与flight_date一致
            result.day_of_week = dayjs.utc(result.flight_date).day() || 7;
        }

        return result;
    } catch (error) {
        console.error("同步航班时间错误:", error, { flight, newFlightDate, timeType });
        return flight;
    }
};

/**
 * 单独同步航班所有类型的时间点
 * 【注意】: 推荐使用 handleFlightDateChange 函数，它提供了更符合规范的实现
 *
 * @param {object} flight - 航班对象
 * @param {string|Date} newFlightDate - 新的航班日期(UTC格式)
 * @returns {object} 更新后的航班对象
 */
export const syncAllFlightTimes = (flight, newFlightDate) => {
    console.warn("syncAllFlightTimes 已不推荐使用，请考虑使用 handleFlightDateChange");
    return syncFlightTimePoints(flight, newFlightDate, "all");
};
